import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { SebastianStatus } from '~/constants/common/SebastianStatus';
import type { DeepPartial } from '~/types';

export type SebastianQuestion = {
  id: string;
  question: string;
  section: string;
};

type SebastianQuestions = {
  questions: SebastianQuestion[];
  question_version: string;
};

export type ContextType = {
  questions: LoadableState<SebastianQuestions>;
  sebastianStatus: SebastianStatus;
  setSebastianStatus: (status: SebastianStatus) => void;
};

const defaultContext: ContextType = {
  questions: defaultLoadableState<SebastianQuestions>({
    questions: [],
    question_version: ''
  }),
  sebastianStatus: SebastianStatus.Loading,
  setSebastianStatus: () => {}
};

export const SebastianContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{}>;

export const SebastianContextProvider = ({ children }: OwnProps) => {
  const [sebastianStatus, setSebastianStatus] = useState(
    SebastianStatus.Loading
  );
  const questions = useLoadableState(defaultContext.questions.data);

  const value = useMemo<ContextType>(
    () => ({
      questions,
      sebastianStatus,
      setSebastianStatus
    }),
    [questions, sebastianStatus]
  );

  return (
    <SebastianContext.Provider value={value}>
      {children}
    </SebastianContext.Provider>
  );
};

SebastianContextProvider.displayName = 'SebastianContextProvider';

export default SebastianContext;

export const generateSebastianContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedSebastianContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue?: Partial<ContextType> }>) {
  return (
    <SebastianContext.Provider value={{ ...defaultContext, ...mockValue }}>
      {children}
    </SebastianContext.Provider>
  );
}

export function useIsGenericQuestions() {
  return (
    useContext(SebastianContext).questions.data.question_version === 'generic'
  );
}

export function useSebastianStatus() {
  return useContext(SebastianContext).sebastianStatus;
}

export function useSetSebastianStatus() {
  return useContext(SebastianContext).setSebastianStatus;
}
